import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { BookOpen, Clock, ArrowRight, Award } from 'lucide-react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LearningHub({ user }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await axios.get(`${API}/learning/modules`);
        setModules(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchModules();
  }, []);

  const categories = [...new Set(modules.map(m => m.category))];

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Learning Hub</h1>
          <p className="text-base text-[#5C665C] mt-2">Master the art of growing your own food</p>
        </div>

        <Link to="/crops" data-testid="learn-handbook-link" className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-[#1A4331]/5 shadow-sm card-hover">
          <div className="w-11 h-11 bg-[#8EAA97]/10 rounded-full flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5 text-[#1A4331]" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-[#1A4331]">The Homegrown Handbook</p>
            <p className="text-sm text-[#5C665C]">Browse every crop &amp; sowing guide</p>
          </div>
          <ArrowRight className="w-4 h-4 text-[#1A4331]" />
        </Link>

        {categories.map(cat => (
          <div key={cat} data-testid={`category-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
            <h2 className="text-2xl font-semibold text-[#1A4331] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{cat}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {modules.filter(m => m.category === cat).map(module => (
                <Link key={module.module_id} to={`/learn/${module.module_id}`} data-testid={`module-${module.module_id}`} className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm card-hover">
                  <div className="w-12 h-12 bg-[#8EAA97]/10 rounded-full flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6 text-[#1A4331]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1A4331] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{module.title}</h3>
                  <p className="text-sm text-[#5C665C] mb-4 leading-relaxed">{module.summary}</p>
                  <div className="flex items-center justify-between text-xs text-[#5C665C]">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {module.duration_min} min</span>
                    <span className="flex items-center gap-1"><Award className="w-3 h-3" /> {module.difficulty}</span>
                    <ArrowRight className="w-4 h-4 text-[#1A4331]" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}

export function LearningModule({ user }) {
  const { moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/learning/modules/${moduleId}`);
        setModule(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [moduleId]);

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div></Layout>;
  if (!module) return <Layout user={user}><div className="text-center py-12">Module not found</div></Layout>;

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto space-y-6">
        <Link to="/learn" className="text-[#5C665C] hover:text-[#1A4331] text-sm">← Back to Learning Hub</Link>
        <div className="bg-white rounded-3xl p-8 border border-[#1A4331]/5 shadow-sm">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <span className="px-3 py-1 bg-[#8EAA97]/20 text-[#1A4331] rounded-full">{module.category}</span>
            <span className="text-[#5C665C]">{module.duration_min} min read</span>
          </div>
          <h1 className="text-4xl font-bold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{module.title}</h1>
          <p className="text-[#5C665C] text-lg mb-8">{module.summary}</p>
          <div className="prose prose-lg max-w-none" data-testid="lesson-content">
            {module.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-semibold text-[#1A4331] mt-6 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{line.slice(3)}</h2>;
              if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-semibold text-[#2D241E] mt-3">{line.slice(2, -2)}</p>;
              if (line.startsWith('- ')) return <li key={i} className="text-[#2D241E] ml-4">{line.slice(2)}</li>;
              if (line.trim()) return <p key={i} className="text-[#2D241E] leading-relaxed my-2">{line}</p>;
              return null;
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default LearningHub;
